import React, { useState } from "react"
import { Flex, Text } from "@capioweb/ui-components/src"
import { Button, Card, DynamicLink } from "@capioweb/ui-components/src"
import { imageBlockDataToImageProps } from "../../../utils"
import { LocalesContext } from "../../../context"
import { NewsArchiveFilterContext } from "../context/NewsArchiveContext"
import { NewsArchiveItem, NewsProps } from ".."
import useSSR from "use-ssr"
import { useQuery } from "react-query"
import { queryStaleTime } from "../../../constants"
import { getAllItems } from "../../../api/getAllItems"
import { useSearchParams } from "react-router-dom"

const NewsCard: React.FC<NewsArchiveItem> = (props) => {
	const srcSetSizes = [{ width: 1000, breakpoint: 2560 }]

	return (
		<Card
			w="100%"
			h={48.5}
			media={
				<>
					{props?.imageDesktopBlock?.media?.value && (
						<img
							{...imageBlockDataToImageProps(
								props?.imageDesktopBlock,
								false,
								true,
								srcSetSizes
							)}
						/>
					)}
				</>
			}
			borderColor="blue33"
		>
			<Card.DateLine>{props.publishingDate}</Card.DateLine>
			<Card.TitleNews>{props.heading.value}</Card.TitleNews>
			<Card.Preamble>{props.preamble.value}</Card.Preamble>
		</Card>
	)
}

const NewsArchiveList: React.FC<NewsProps> = (props) => {
	const { translate, translationKeys } = React.useContext(LocalesContext)
	const { activeOrganisationIds, activeNewsSubjectIds } = React.useContext(
		NewsArchiveFilterContext
	)
	const [activeNews, setActiveNews] = React.useState(props.items)
	const [showAll, setShowAll] = useState(false)
	const initialItemLimit = 6
	const prevFilterValues = React.useRef({
		activeOrganisationIds,
		activeNewsSubjectIds,
	})
	const [searchParams] = useSearchParams()
	const nextQueryValue = searchParams.get("next") === "true"

	const { isBrowser } = useSSR()

	const resetNewsList = (activeOrganisationIds, activeNewsSubjectIds) => {
		setActiveNews(showAll ? data?.items ?? props.items : props.items)
		prevFilterValues.current = { activeOrganisationIds, activeNewsSubjectIds }
	}

	const getActiveNewsByFilter = (
		items: NewsArchiveItem[],
		activeOrganisationIds: number[],
		activeNewsSubjectIds: number[]
	) => {
		let activeNews = items

		if (activeOrganisationIds.length > 0) {
			let activeNewsByOrganisation = []
			items.map((item) => {
				item.organisationIds.map(
					(id) =>
						activeOrganisationIds.includes(id) &&
						!activeNewsByOrganisation.includes(item) &&
						activeNewsByOrganisation.push(item)
				)
			}, (activeNews = activeNewsByOrganisation))
		}

		if (activeNewsSubjectIds.length > 0) {
			let activeNewsBySubjects = []
			activeNews.map((item) => {
				item.newsSubjectsIds.map(
					(id) =>
						activeNewsSubjectIds.includes(id) &&
						!activeNews.includes(item) &&
						activeNewsBySubjects.push(item)
				)
			}, (activeNews = activeNewsBySubjects))
		}

		return activeNews
	}

	React.useEffect(() => {
		const hasActiveFilters =
			activeOrganisationIds.length === 0 && activeNewsSubjectIds.length === 0
				? false
				: true

		// Reset news archive list if we don't have any active filters
		if (!hasActiveFilters) {
			resetNewsList(activeOrganisationIds, activeNewsSubjectIds)
			return
		}

		// Filter by active organisation id if activeOrganisationIds has changed
		if (
			prevFilterValues.current.activeOrganisationIds !==
				activeOrganisationIds ||
			prevFilterValues.current.activeNewsSubjectIds !== activeNewsSubjectIds
		) {
			const activeNews = getActiveNewsByFilter(
				data?.items ?? props.items,
				activeOrganisationIds,
				activeNewsSubjectIds
			)
			setShowAll(true)
			setActiveNews(activeNews)
		}
	}, [activeOrganisationIds, activeNewsSubjectIds])

	const { data, isLoading } = useQuery(
		"newsarchive",
		() => (isBrowser || nextQueryValue) && getAllItems("newsarchive"),
		{
			staleTime: queryStaleTime,
		}
	)

	return (
		<>
			<Flex justifyContent="center">
				<Text variant="paragraph-16" mb={4.5} mt={0.5}>
					{activeNews.length > 1
						? translate(translationKeys.newsResultCount)?.replace(
								/%s/g,
								!showAll
									? props.totalItems.toString()
									: activeNews.length.toString()
						  )
						: translate(translationKeys.newsResultCountSingular)?.replace(
								/%s/g,
								!showAll
									? props.totalItems.toString()
									: activeNews.length.toString()
						  )}
					:
				</Text>
			</Flex>
			<Flex
				w={1}
				flexWrap="wrap"
				justifyContent="flex-start"
				alignContent="space-between"
				columnGap={{ m: 3 }}
				rowGap={{ _: 2, m: 3 }}
			>
				{activeNews.length > 0 &&
					activeNews.map((block, i) => {
						return (
							<Flex
								key={block.contentLink?.id ?? i}
								flexGrow={0}
								flexBasis={{
									_: "100%",
									m: "calc(50% - 12px)",
									l: "calc(100% / 3 - 16px)",
								}}
							>
								<DynamicLink url={block?.contentLink?.url ?? "#"}>
									<NewsCard {...block} />
								</DynamicLink>
							</Flex>
						)
					})}
			</Flex>
			<Flex w={1} justifyContent="center">
				{!showAll && activeNews.length >= initialItemLimit && (
					<Button.Link
						mt={{ _: 2, m: 3 }}
						variant="ghost"
						icon="plus"
						textColor="navy"
						onClick={(e) => {
							e.preventDefault()
							setShowAll(true)
							setActiveNews(data?.items ?? props.items)
						}}
						to="?next=true"
						disabled={isLoading}
					>
						{isLoading
							? translate(translationKeys.loadNews)
							: translate(translationKeys.showMore)}
					</Button.Link>
				)}
			</Flex>
		</>
	)
}

export { NewsArchiveList }
